import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardList,
  Search,
  ChevronLeft,
  ChevronRight,
  Download,
  Loader2,
  Eye,
  User,
  Calendar,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/demo-data";
import { toast } from "sonner";

const ACTION_COLORS: Record<string, string> = {
  ARTISAN_APPROVE: "bg-green-100 text-green-800",
  ARTISAN_REJECT: "bg-red-100 text-red-800",
  ARTISAN_SUSPEND: "bg-orange-100 text-orange-800",
  MESSAGE_MODERATION: "bg-yellow-100 text-yellow-800",
  QUOTE_FLAGGED_SUSPECT: "bg-amber-100 text-amber-800",
  RC_PRO_VERIFY: "bg-blue-100 text-blue-800",
  TRIAL_ALERT_MANUAL_RELANCE: "bg-purple-100 text-purple-800",
  TRIAL_ALERT_MARK_CONTACTED: "bg-indigo-100 text-indigo-800",
};

function getActionColor(action: string): string {
  return ACTION_COLORS[action] ?? "bg-gray-100 text-gray-800";
}

function formatStaffName(user: { email: string; firstName?: string | null; lastName?: string | null } | null): string {
  if (!user) return "—";
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email;
}

export default function AuditLog() {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [staffFilter, setStaffFilter] = useState("");
  const [detailId, setDetailId] = useState<string | null>(null);
  const limit = 20;

  const { data, isLoading } = trpc.admin.audit.list.useQuery({
    page,
    limit,
    action: actionFilter || undefined,
    entityType: entityTypeFilter || undefined,
    actorStaffUserId: staffFilter || undefined,
  });

  const { data: detail, isLoading: detailLoading } = trpc.admin.audit.get.useQuery(
    { id: detailId! },
    { enabled: !!detailId }
  );

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  function handleExportCsv() {
    const params = new URLSearchParams();
    if (actionFilter) params.set("action", actionFilter);
    if (entityTypeFilter) params.set("entityType", entityTypeFilter);
    if (staffFilter) params.set("actorStaffUserId", staffFilter);
    toast.info("Export en cours...");
  }

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Chargement des logs…
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6" />
              Journal d'audit
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Historique de toutes les actions staff — qui, quoi, quand
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportCsv}>
            <Download className="h-4 w-4 mr-1" />
            Exporter CSV
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filtrer par action…"
                  value={actionFilter}
                  onChange={(e) => {
                    setActionFilter(e.target.value);
                    setPage(1);
                  }}
                  className="h-8 w-48 text-sm"
                />
              </div>
              <Select
                value={entityTypeFilter}
                onValueChange={(v) => {
                  setEntityTypeFilter(v === "ALL" ? "" : v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-8 w-44 text-sm">
                  <SelectValue placeholder="Type d'entité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Tous les types</SelectItem>
                  <SelectItem value="Company">Company</SelectItem>
                  <SelectItem value="User">User</SelectItem>
                  <SelectItem value="PrivateProject">Projet privé</SelectItem>
                  <SelectItem value="PrivateProjectQuote">Devis</SelectItem>
                  <SelectItem value="Message">Message</SelectItem>
                  <SelectItem value="Tender">Appel d'offres</SelectItem>
                </SelectContent>
              </Select>
              {(actionFilter || entityTypeFilter || staffFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => {
                    setActionFilter("");
                    setEntityTypeFilter("");
                    setStaffFilter("");
                    setPage(1);
                  }}
                >
                  Réinitialiser
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold">{data?.total ?? 0}</div>
              <div className="text-xs text-muted-foreground">Total entrées</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold">{data?.items?.length ?? 0}</div>
              <div className="text-xs text-muted-foreground">Cette page</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold">{page}</div>
              <div className="text-xs text-muted-foreground">Page actuelle</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="py-4 text-center">
              <div className="text-2xl font-bold">{totalPages}</div>
              <div className="text-xs text-muted-foreground">Total pages</div>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Logs d'audit
              <Badge variant="secondary" className="text-xs ml-auto">
                {data?.total ?? 0} entrées
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {!data?.items?.length ? (
              <div className="py-12 text-center text-muted-foreground text-sm">
                Aucune entrée trouvée
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs w-[140px]">Date</TableHead>
                    <TableHead className="text-xs">Staff</TableHead>
                    <TableHead className="text-xs">Action</TableHead>
                    <TableHead className="text-xs">Type entité</TableHead>
                    <TableHead className="text-xs">ID entité</TableHead>
                    <TableHead className="text-xs w-[60px]">Détail</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((log: any) => (
                    <TableRow key={log.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setDetailId(log.id)}>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          {formatDate(log.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3 text-muted-foreground" />
                          {formatStaffName(log.User)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`text-xs ${getActionColor(log.action)}`}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {log.entityType}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground truncate max-w-[120px]">
                        {log.entityId}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDetailId(log.id);
                          }}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!detailId} onOpenChange={(open) => { if (!open) setDetailId(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Détail de l'action
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              Chargement…
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Date</div>
                  <div>{formatDate(detail.createdAt)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Staff</div>
                  <div>{formatStaffName(detail.User)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Action</div>
                  <Badge className={`text-xs ${getActionColor(detail.action)}`}>
                    {detail.action}
                  </Badge>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Type entité</div>
                  <Badge variant="outline" className="text-xs">
                    {detail.entityType}
                  </Badge>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">ID entité</div>
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{detail.entityId}</code>
                </div>
                {detail.companyId && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Company ID</div>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{detail.companyId}</code>
                  </div>
                )}
                {detail.ip && (
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">IP</div>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{detail.ip}</code>
                  </div>
                )}
              </div>

              {detail.beforeJson && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Avant</div>
                  <pre className="text-xs bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-[200px]">
                    {JSON.stringify(detail.beforeJson, null, 2)}
                  </pre>
                </div>
              )}

              {detail.afterJson && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-1">Après</div>
                  <pre className="text-xs bg-green-50 border border-green-200 rounded p-3 overflow-auto max-h-[200px]">
                    {JSON.stringify(detail.afterJson, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailId(null)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
